import { Head, Link, router } from "@inertiajs/react"
import {
  ChevronDown,
  CirclePlus,
  ClipboardList,
  Contact,
  SquarePen,
} from "lucide-react"
import { useState } from "react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import { SortableSubjects } from "@/components/sortable-subjects"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import { formatPhone } from "@/lib/format-phone"
import { objectPositionStyle } from "@/lib/utils"
import {
  detailCaseBillingReportsPath,
  selectFinalReportsCaseReportsPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  file_number: string | null
  case_reference: string | null
  hpi_name: string | null
  qb_name: string | null
  report_name: string | null
  directory: string | null
  display_name: string
  customer_id: number
  customer_name: string | null
  customer_user_id: number | null
  customer_user_name: string | null
  customer_location_id: number | null
  customer_location_name: string | null
  investigation_type_id: number | null
  investigation_type: string | null
  intake_by_id: number | null
  intake_by: string | null
  rep: string | null
  manager_id: number | null
  manager: string | null
  referral_customer_id: number | null
  referral_customer_name: string | null
  referral_customer_user_id: number | null
  referral_customer_user_name: string | null
  initial_case_id: number | null
  initial_case_number: string | null
  location_city: string | null
  location_state: string | null
  open_date: string | null
  closed_date: string | null
  accepted_date: string | null
  paid_date: string | null
  budget: number | null
  retainer: number | null
  case_total: number | null
  to_be_billed: number | null
  invoiced: number | null
  results: number | null
  results_notes: string | null
  notes: string | null
  objectives: string | null
  internal_notes: string | null
  customer_phone: string | null
  customer_email: string | null
  customer_address: string | null
  customer_user_phone: string | null
  customer_user_email: string | null
  has_attorney: boolean
  attorney_name: string | null
  attorney_phone: string | null
  attorney_law_firm: string | null
  attorney_email: string | null
  prepared_for: string | null
  matter: string | null
  accepted: number | null
  accepted_text: string | null
  after_hours: boolean
  is_public: boolean
  hpi_company: number | null
  source: string | null
  status: number
  display_status: string
  open: boolean
  created_at: string
  updated_at: string
  subjects_count: number
  employees_count: number
  contacts_count: number
}

interface CaseLogEntry {
  id: number
  log_type: string
  log_date: string | null
  title: string | null
  content_preview: string | null
  user_name: string | null
  status: number
  urgent: boolean
  sensitive: boolean
  significant: boolean
  completed: boolean
  completed_on: string | null
  due_date: string | null
}

interface SubjectSummary {
  id: number
  subject_type: string | null
  display_name: string
  sex: string | null
  race: string | null
  date_of_birth: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  thumb_url: string | null
  focal_point_x?: number | null
  focal_point_y?: number | null
  has_photo: boolean
  photos_count: number
}

interface MenuCounts {
  tasks: number
  updates: number
  accounting_notes: number
  restricted_notes: number
  schedules: number
  equipment: number
  evidence: number
  expenses: number
  gps: number
  time_entries: number
  daily_reports: number
  gps_reports: number
  incident_reports: number
  documents: number
  photos: number
  contacts: number
}

interface AddPermissions {
  case_status: number
  can_add_case_notes: boolean
  can_update_contact: boolean
  can_add_schedule: boolean
  can_add_people: boolean
  can_prepare_reports: boolean
  can_add_restricted: boolean
  is_co: boolean
  can_view_final_report: boolean
  can_view_billing_report: boolean
}

const CASE_STATUS = {
  POTENTIAL: 0,
  ACTIVE: 1,
  CLOSED: 2,
  PENDING_REPORT: 3,
  PENDING_PAYMENT: 4,
} as const

interface ClockInEntry {
  id: number
  started_at: string
}

interface ServiceItemType {
  id: number
  name: string
}

interface Props {
  case_record: CaseRecord
  case_logs: CaseLogEntry[]
  logs_count: number
  subjects: SubjectSummary[]
  can_sort_subjects: boolean
  can_view_supervisor_notes: boolean
  is_admin: boolean
  menu_counts: MenuCounts
  add_permissions: AddPermissions
  clock_in_entry: ClockInEntry | null
  service_item_types: ServiceItemType[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number | null): string {
  if (amount === null) return "-"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function raceGenderLabel(race: string | null, sex: string | null): string {
  const parts: string[] = []
  if (race) parts.push(race)
  if (sex) parts.push(sex === "M" ? "Male" : sex === "F" ? "Female" : sex)
  return parts.join(", ")
}

/* ── Photo Placeholder ─────────────────────────────────── */

function PhotoPlaceholder() {
  return (
    <div
      className="bg-hpi-light-gray flex h-[56px] w-[56px] items-center justify-center rounded"
      title="Photo Not Available"
    >
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        className="text-hpi-med-gray"
      >
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle
          cx="8.5"
          cy="8.5"
          r="1.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M6 17l3.5-4.5 2.5 3L15.5 11 19 17H6z"
          fill="currentColor"
          opacity="0.3"
        />
      </svg>
    </div>
  )
}

function SubjectThumb({ row }: { row: SubjectSummary }) {
  if (row.thumb_url) {
    return (
      <img
        src={row.thumb_url}
        alt={row.display_name}
        className="h-[56px] w-[56px] rounded object-cover"
        style={{ objectPosition: objectPositionStyle(row) }}
      />
    )
  }
  return <PhotoPlaceholder />
}

/* ── People Columns ────────────────────────────────────── */

const peopleColumns: HpiColumn<SubjectSummary>[] = [
  {
    header: "Type",
    width: "max-w-[80px]",
    render: (row) => (
      <span className="text-xs font-medium">
        {row.subject_type ?? "Subject"}
      </span>
    ),
  },
  {
    header: "",
    width: "max-w-[72px]",
    render: (row) => <SubjectThumb row={row} />,
  },
  {
    header: "Name",
    subHeader: "DOB / Ethnicity, Gender",
    render: (row) => {
      const rg = raceGenderLabel(row.race, row.sex)
      return (
        <div>
          <div className="text-xs font-semibold">{row.display_name}</div>
          {rg && <div className="text-hpi-med-gray text-xs">{rg}</div>}
        </div>
      )
    },
  },
  {
    header: "Street Address",
    subHeader: "City, State, ZIP",
    render: (row) => {
      const street = [row.address, row.address2].filter(Boolean).join(", ")
      const csz = [row.city, [row.state, row.zip].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", ")
      if (!street && !csz)
        return <span className="text-hpi-med-gray">Unknown</span>
      return (
        <div>
          {street && <div className="text-xs">{street}</div>}
          {csz && <div className="text-xs">{csz}</div>}
        </div>
      )
    },
  },
]

/* ── Top Bar Helpers ────────────────────────────────────── */

function TabLink({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors ${className}`}
    >
      {children}
    </a>
  )
}

function TabDropdown({
  label,
  items,
}: {
  label: string
  items: { label: string; href: string }[]
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors outline-none">
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((item) => (
          <DropdownMenuItem key={item.href} asChild>
            <a href={item.href} className="cursor-pointer">
              {item.label}
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function buildAddItems(
  caseId: number,
  p: AddPermissions,
): { label: string; href: string }[] {
  const s = p.case_status
  const items: { label: string; href: string }[] = []

  const canExpense =
    s === CASE_STATUS.ACTIVE ||
    (s >= CASE_STATUS.PENDING_REPORT && p.can_prepare_reports)

  if (p.is_co)
    items.push({
      label: "Accounting Note",
      href: `/cases/${caseId}/logs/new?type=accounting`,
    })
  if (p.can_add_case_notes)
    items.push({
      label: "Case Update",
      href: `/cases/${caseId}/logs/new?type=update`,
    })
  if (p.can_update_contact && s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Contact Info",
      href: `/cases/${caseId}/contacts/new`,
    })
  if (s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Documents",
      href: `/cases/${caseId}/documents/new`,
    })
  if (s !== CASE_STATUS.CLOSED)
    items.push({
      label: "Equipment/GPS",
      href: `/cases/${caseId}/equipment_assignments/new`,
    })
  if (canExpense) {
    items.push({ label: "Expense", href: `/cases/${caseId}/expenses/new` })
    items.push({
      label: "Per Diem",
      href: `/cases/${caseId}/expenses/new?per_diem=true`,
    })
    items.push({
      label: "Case Photos",
      href: `/cases/${caseId}/photos/new`,
    })
    items.push({ label: "Reports", href: `/cases/${caseId}/reports/new` })
  }
  if (p.can_add_restricted)
    items.push({
      label: "Restricted Note",
      href: `/cases/${caseId}/logs/new?type=restricted`,
    })
  if (p.can_add_schedule && s <= CASE_STATUS.ACTIVE)
    items.push({ label: "Schedule", href: `/cases/${caseId}/schedules/new` })
  if (p.can_add_people && s <= CASE_STATUS.ACTIVE)
    items.push({
      label: "Subject/People",
      href: `/cases/${caseId}/subjects/new`,
    })
  if (s !== CASE_STATUS.CLOSED)
    items.push({ label: "Task", href: `/cases/${caseId}/logs/new?type=todo` })
  if (canExpense)
    items.push({ label: "Time", href: `/cases/${caseId}/time_entries/new` })
  return items.sort((a, b) => a.label.localeCompare(b.label))
}

/* ── Case Log Columns ───────────────────────────────────── */

const logColumns: HpiColumn<CaseLogEntry>[] = [
  {
    header: "Date",
    subHeader: "Time",
    width: "max-w-[100px]",
    render: (row) => (
      <div>
        <div className="text-xs font-medium">{formatDate(row.log_date)}</div>
      </div>
    ),
  },
  {
    header: "Description",
    render: (row) => (
      <div>
        <span className="text-xs font-semibold uppercase">{row.log_type}</span>
        {row.content_preview && (
          <div className="text-hpi-med-blue mt-0.5 text-xs">
            {row.content_preview}
          </div>
        )}
      </div>
    ),
  },
]

/* ── Main Component ─────────────────────────────────────── */

export default function Show({
  case_record,
  case_logs,
  logs_count,
  subjects,
  can_sort_subjects,
  can_view_supervisor_notes,
  is_admin,
  menu_counts: mc,
  add_permissions: ap,
  clock_in_entry,
  service_item_types,
}: Props) {
  const c = case_record
  const [clockInServiceTypeId, setClockInServiceTypeId] = useState<string>("")

  const handleClockIn = () => {
    if (!clockInServiceTypeId) return
    router.post(`/cases/${c.id}/time_entries/clock_in`, {
      service_item_type_id: clockInServiceTypeId,
    })
  }
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
  ]

  /* -- Build Updates dropdown items -- */
  const updatesItems: { label: string; href: string }[] = []
  if (mc.updates > 0)
    updatesItems.push({
      label: `Updates (${mc.updates})`,
      href: `/cases/${c.id}/logs?type=update`,
    })
  if (mc.accounting_notes > 0 && ap.is_co)
    updatesItems.push({
      label: `Accounting Notes (${mc.accounting_notes})`,
      href: `/cases/${c.id}/logs?type=accounting`,
    })
  if (mc.restricted_notes > 0 && ap.can_add_restricted)
    updatesItems.push({
      label: `Restricted Notes (${mc.restricted_notes})`,
      href: `/cases/${c.id}/logs?type=restricted`,
    })

  /* -- Build Activity dropdown items -- */
  const activityItems: { label: string; href: string }[] = []
  if (mc.schedules > 0)
    activityItems.push({
      label: `Scheduled Work (${mc.schedules})`,
      href: `/cases/${c.id}/schedules`,
    })
  if (mc.equipment > 0)
    activityItems.push({
      label: `Equipment (${mc.equipment})`,
      href: `/cases/${c.id}/equipment_assignments`,
    })
  if (mc.evidence > 0)
    activityItems.push({
      label: `Evidence (${mc.evidence})`,
      href: `/cases/${c.id}/evidences`,
    })
  if (mc.expenses > 0)
    activityItems.push({
      label: `Expenses (${mc.expenses})`,
      href: `/cases/${c.id}/expenses`,
    })
  if (mc.gps > 0)
    activityItems.push({
      label: `GPS (${mc.gps})`,
      href: `/cases/${c.id}/gps`,
    })
  if (mc.time_entries > 0)
    activityItems.push({
      label: `Time (${mc.time_entries})`,
      href: `/cases/${c.id}/time_entries`,
    })
  /* -- Build Reports dropdown items -- */
  const reportsItems: { label: string; href: string }[] = []
  if (mc.daily_reports > 0)
    reportsItems.push({
      label: `Daily Reports (${mc.daily_reports})`,
      href: `/cases/${c.id}/reports?category=daily`,
    })
  if (mc.gps_reports > 0)
    reportsItems.push({
      label: `GPS Reports (${mc.gps_reports})`,
      href: `/cases/${c.id}/reports?category=gps`,
    })
  if (mc.incident_reports > 0)
    reportsItems.push({
      label: `Incident Reports (${mc.incident_reports})`,
      href: `/cases/${c.id}/reports?category=incident`,
    })
  if (ap.can_view_final_report)
    reportsItems.push({
      label: "Final Report",
      href: selectFinalReportsCaseReportsPath(c.id),
    })
  if (ap.can_view_billing_report)
    reportsItems.push({
      label: "Billing Report",
      href: detailCaseBillingReportsPath(c.id),
    })

  /* -- Build Add dropdown -- */
  const addItems = buildAddItems(c.id, ap)

  const caseTabsBar = (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-6">
        {/* Worksheet - always visible */}
        <a
          href={`/cases/${c.id}/worksheet`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1 text-sm transition-colors"
        >
          <ClipboardList className="h-4 w-4" />
          <span>Worksheet</span>
        </a>

        {/* Tasks - visible when count > 0, styled red */}
        {mc.tasks > 0 && (
          <a
            href={`/cases/${c.id}/logs?type=todo`}
            className="flex items-center gap-1 text-sm text-red-600 transition-colors hover:text-red-800"
          >
            Tasks ({mc.tasks})
          </a>
        )}

        {/* Updates dropdown */}
        {updatesItems.length > 0 && (
          <TabDropdown label="Updates" items={updatesItems} />
        )}

        {/* Activity dropdown */}
        {activityItems.length > 0 && (
          <TabDropdown label="Activity" items={activityItems} />
        )}

        {/* Reports dropdown */}
        {reportsItems.length > 0 && (
          <TabDropdown label="Reports" items={reportsItems} />
        )}

        {/* Documents link */}
        {mc.documents > 0 && (
          <TabLink href={`/cases/${c.id}/documents`}>
            Documents ({mc.documents})
          </TabLink>
        )}

        {/* Case Photos link */}
        {mc.photos > 0 && (
          <TabLink href={`/cases/${c.id}/photos`}>
            Case Photos ({mc.photos})
          </TabLink>
        )}

        {/* Contacts link */}
        {mc.contacts > 0 && (
          <TabLink href={`/cases/${c.id}/contacts`}>
            Contacts ({mc.contacts})
          </TabLink>
        )}
      </div>

      {/* Add dropdown */}
      {addItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className="text-hpi-med-blue hover:text-hpi-active-blue flex items-center gap-1.5 text-sm outline-none">
            <CirclePlus className="h-4 w-4" />
            <span>Add</span>
            <ChevronDown className="h-3 w-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {addItems.map((item) => (
              <DropdownMenuItem key={item.href} asChild>
                <a href={item.href} className="cursor-pointer">
                  {item.label}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )

  return (
    <AppLayout topBar={caseTabsBar}>
      <Head title={c.display_name} />

      <div className="p-6">
        {/* Breadcrumbs */}
        <div className="mb-6">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>

        {/* Case header info - two columns */}
        <div className="mb-6 flex gap-8">
          {/* Left column: subject & customer info */}
          <div className="flex-1">
            <div className="mb-4 flex items-center gap-3">
              <h1 className="text-hpi-dark-gray text-xl font-semibold uppercase">
                {c.display_name}
              </h1>
              <Link
                href={`/cases/${c.id}/edit`}
                className="text-hpi-med-blue hover:text-hpi-active-blue"
              >
                <SquarePen className="h-4 w-4" />
              </Link>
            </div>

            <div className="text-hpi-dark-gray space-y-1 text-sm">
              <p>
                <span className="font-semibold">CUSTOMER:</span>{" "}
                <Link
                  href={`/customers/${c.customer_id}`}
                  className="hover:text-hpi-active-blue"
                >
                  {c.customer_name}
                </Link>
              </p>
              {c.customer_phone && (
                <p>
                  <span className="font-semibold">PHONE:</span>{" "}
                  {formatPhone(c.customer_phone)}
                </p>
              )}
              {c.customer_email && (
                <p>
                  <span className="font-semibold">EMAIL:</span>{" "}
                  {c.customer_email}
                </p>
              )}
              {c.customer_address && (
                <p>
                  <span className="font-semibold">ADDRESS:</span>{" "}
                  {c.customer_address}
                </p>
              )}
            </div>

            {/* Customer contact */}
            {c.customer_user_name && (
              <div className="text-hpi-dark-gray mt-4 space-y-1 text-sm">
                <p>
                  <span className="font-semibold">CONTACT:</span>{" "}
                  {c.customer_user_name}
                </p>
                {c.customer_user_phone && (
                  <p>
                    <span className="font-semibold">PHONE:</span>{" "}
                    {formatPhone(c.customer_user_phone)}
                  </p>
                )}
                {c.customer_user_email && (
                  <p>
                    <span className="font-semibold">EMAIL:</span>{" "}
                    {c.customer_user_email}
                  </p>
                )}
              </div>
            )}

            {/* Attorney info */}
            {c.has_attorney && (
              <div className="text-hpi-dark-gray mt-4 space-y-1 text-sm">
                {c.attorney_name && (
                  <p>
                    <span className="font-semibold">CUSTOMER ATTORNEY:</span>{" "}
                    {c.attorney_name}
                  </p>
                )}
                {c.attorney_phone && (
                  <p>
                    <span className="font-semibold">ATTORNEY PHONE:</span>{" "}
                    {formatPhone(c.attorney_phone)}
                  </p>
                )}
                {c.attorney_law_firm && (
                  <p>
                    <span className="font-semibold">LAW FIRM:</span>{" "}
                    {c.attorney_law_firm}
                  </p>
                )}
                {c.attorney_email && (
                  <p>
                    <span className="font-semibold">ATTORNEY EMAIL:</span>{" "}
                    {c.attorney_email}
                  </p>
                )}
              </div>
            )}

            {/* Financial summary boxes — admin only */}
            {is_admin && (
              <div className="mt-6 flex gap-4">
                <div className="border-hpi-light-gray min-w-[120px] border-t-2 bg-white px-4 py-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-hpi-active-blue text-xs font-medium">
                        Billable
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Unbilled time entries + unbilled expenses
                    </TooltipContent>
                  </Tooltip>
                  <div className="text-hpi-dark-blue mt-1 text-2xl font-light">
                    {formatCurrency(c.to_be_billed)}
                  </div>
                </div>
                <div className="border-hpi-light-gray min-w-[120px] border-t-2 bg-white px-4 py-3">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-hpi-active-blue text-xs font-medium">
                        Case Total
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      All time entries + billed expenses
                    </TooltipContent>
                  </Tooltip>
                  <div className="text-hpi-dark-blue mt-1 text-2xl font-light">
                    {formatCurrency(c.case_total)}
                  </div>
                </div>
              </div>
            )}

            {c.referral_customer_name && (
              <p className="text-hpi-dark-gray mt-4 text-sm">
                <span className="font-semibold">REFERRAL CREDIT:</span>{" "}
                <Link
                  href={`/customers/${c.referral_customer_id}`}
                  className="hover:text-hpi-active-blue"
                >
                  {c.referral_customer_name}
                </Link>
                {c.referral_customer_user_name && (
                  <span>: {c.referral_customer_user_name}</span>
                )}
              </p>
            )}
          </div>

          {/* Right column: status & metadata */}
          <div className="w-[340px] shrink-0">
            <div className="mb-4">
              <span
                className={`text-lg font-medium ${c.open ? "text-hpi-active-blue" : "text-hpi-med-gray"}`}
              >
                {c.display_status} - {c.investigation_type ?? ""}
              </span>
            </div>

            <div className="text-hpi-dark-gray space-y-1 text-sm">
              <p>
                <span className="font-semibold">INTAKE BY:</span>{" "}
                {c.intake_by ?? "-"}
              </p>
              <p>
                <span className="font-semibold">REP:</span> {c.rep ?? "-"}
              </p>
              <p>
                <span className="font-semibold">MANAGER:</span>{" "}
                {c.manager ?? "-"}
              </p>
              <p>
                <span className="font-semibold">OPENED:</span>{" "}
                {formatDate(c.open_date)}
              </p>
              <p>
                <span className="font-semibold">CASE #:</span> {c.case_number}
              </p>
            </div>

            {c.objectives && (
              <div className="text-hpi-dark-gray mt-4 text-sm">
                <p>
                  <span className="font-semibold">OBJECTIVE:</span>{" "}
                  {c.objectives}
                </p>
              </div>
            )}

            {(c.accepted_text ?? c.results_notes) && (
              <div className="text-hpi-dark-gray mt-2 text-sm">
                <p>
                  <span className="font-semibold">FINAL ASSESSMENT:</span>{" "}
                  {c.accepted_text && (
                    <span className="text-hpi-dark-green font-semibold">
                      {c.accepted_text}
                    </span>
                  )}
                  {c.accepted_text && c.results_notes && " - "}
                  {c.results_notes}
                </p>
              </div>
            )}

            {can_view_supervisor_notes && c.internal_notes && (
              <div className="mt-4 text-sm whitespace-pre-line text-red-700">
                <span className="font-semibold">SUPERVISOR NOTES:</span>{" "}
                {c.internal_notes}
              </div>
            )}

            {/* Clock in / Add Time buttons */}
            <div className="mt-6 flex flex-col gap-2">
              {clock_in_entry ? (
                <Link
                  href={`/cases/${c.id}/time_entries/clock_out`}
                  method="post"
                  as="button"
                  className="bg-hpi-red rounded px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Clock Out (since {formatTime(clock_in_entry.started_at)})
                </Link>
              ) : (
                <div className="flex flex-col gap-1">
                  <select
                    value={clockInServiceTypeId}
                    onChange={(e) => setClockInServiceTypeId(e.target.value)}
                    className="border-hpi-light-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue rounded-md border px-3 py-2 text-sm shadow-sm"
                    aria-label="Service type"
                  >
                    <option value="">--- Select service type ---</option>
                    {service_item_types.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleClockIn}
                    disabled={!clockInServiceTypeId}
                    className="rounded bg-green-600 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Clock In
                  </button>
                  <p className="text-hpi-med-gray text-xs">
                    Select a service type to clock in.
                  </p>
                </div>
              )}
              <Link
                href={`/cases/${c.id}/time_entries/new`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-6 py-2 text-center text-sm font-medium text-white transition-colors"
              >
                Add Time
              </Link>
            </div>
          </div>
        </div>

        {/* Two-column tables: People + Case Updates */}
        <div className="grid grid-cols-2 gap-4">
          {/* People table */}
          {can_sort_subjects ? (
            <div>
              <div className="bg-hpi-dark-blue flex items-center justify-between rounded-t px-4 py-2.5">
                <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
                  People
                </h3>
                <a
                  href={`/cases/${c.id}/subjects/new`}
                  className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white"
                >
                  <Contact className="h-4 w-4" />
                  <span>New/Add</span>
                </a>
              </div>
              <div className="bg-white">
                {subjects.length === 0 ? (
                  <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
                    No records found.
                  </div>
                ) : (
                  <SortableSubjects caseId={c.id} subjects={subjects} />
                )}
              </div>
            </div>
          ) : (
            <HpiDataTable<SubjectSummary>
              title="People"
              actions={[
                {
                  label: "New/Add",
                  icon: Contact,
                  href: `/cases/${c.id}/subjects/new`,
                },
              ]}
              columns={peopleColumns}
              data={subjects}
              keyFn={(row) => row.id}
              rowHref={(row) => `/cases/${c.id}/subjects/${row.id}`}
            />
          )}

          {/* Case Updates table */}
          <HpiDataTable<CaseLogEntry>
            title="Case Updates"
            actions={[
              {
                label: "New/Add",
                icon: SquarePen,
                href: `/cases/${c.id}/logs/new?type=update`,
              },
            ]}
            columns={logColumns}
            data={case_logs.slice(0, 5)}
            keyFn={(row) => row.id}
            rowHref={(row) => `/cases/${c.id}/logs/${row.id}`}
          />
        </div>

        {/* View all logs link */}
        {logs_count > 5 && (
          <div className="mt-3 text-center">
            <Link
              href={`/cases/${c.id}/logs`}
              className="text-hpi-active-blue text-sm hover:underline"
            >
              View all {logs_count} updates
            </Link>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
